
import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    revenue: '',
    challenges: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d41r02457hvodmunvkf0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          revenue: '',
          challenges: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent text-sm"
              placeholder="Your full name"
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent text-sm"
              placeholder="your@email.com"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
              Company Name *
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent text-sm"
              placeholder="Your company name"
            />
          </div>
          
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent text-sm"
              placeholder="(555) 123-4567"
            />
          </div>
        </div>

        <div>
          <label htmlFor="revenue" className="block text-sm font-medium text-gray-700 mb-2">
            Annual Revenue Range
          </label>
          <select
            id="revenue"
            name="revenue"
            value={formData.revenue}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent text-sm pr-8"
          >
            <option value="">Select revenue range</option>
            <option value="under-100k">Under $100K</option>
            <option value="100k-500k">$100K - $500K</option>
            <option value="500k-1m">$500K - $1M</option>
            <option value="1m-5m">$1M - $5M</option>
            <option value="5m-plus">$5M+</option>
          </select>
        </div>

        <div>
          <label htmlFor="challenges" className="block text-sm font-medium text-gray-700 mb-2">
            Biggest Revenue Challenge
          </label>
          <select
            id="challenges"
            name="challenges"
            value={formData.challenges}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent text-sm pr-8"
          >
            <option value="">Select your biggest challenge</option>
            <option value="inconsistent-leads">Inconsistent lead generation</option>
            <option value="founder-dependency">Too dependent on founder for sales</option>
            <option value="low-conversion">Low conversion rates</option>
            <option value="scaling-issues">Difficulty scaling beyond current level</option>
            <option value="manual-processes">Too many manual processes</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            Tell us about your goals
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent text-sm resize-none"
            placeholder="What would you like to achieve with predictable revenue systems? (500 characters max)"
          />
          <div className="text-right text-xs text-gray-500 mt-1">
            {formData.message.length}/500 characters
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting || formData.message.length > 500}
          className="w-full bg-[#2BB6A3] text-white py-4 px-6 rounded-lg font-semibold hover:bg-[#239B8A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isSubmitting ? 'Scheduling Your Assessment...' : 'Get My Free Assessment'}
        </button>

        {submitStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <i className="ri-check-circle-line text-green-500 text-xl mr-3"></i>
              <div>
                <h4 className="font-semibold text-green-800">Assessment Scheduled!</h4>
                <p className="text-green-700 text-sm">We'll contact you within 24 hours to schedule your free revenue assessment.</p>
                <p className="text-green-700 text-sm mt-2">
                  Or <a href="/book-call" className="underline font-semibold">book a call directly</a> with James Hansen.
                </p>
              </div>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
              <div>
                <h4 className="font-semibold text-red-800">Something went wrong</h4>
                <p className="text-red-700 text-sm">Please try again or call us directly at (916) 555-0123.</p>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
